import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    LineChart,
    Line,
    BarChart,
    Bar,
    PieChart,
    Pie,
    Cell,
    XAxis,
    YAxis,
    Tooltip,
    Legend,
    ResponsiveContainer,
    CartesianGrid,
} from "recharts";
import CountUp from "react-countup";
import { useInView } from "react-intersection-observer";
import {
    TrendingUp,
    TrendingDown,
    Minus,
    BarChart3,
    Building2,
    Wallet,
    Plane,
    BedDouble,
    MapPin,
} from "lucide-react";
import api from "../../lib/api";
import useSeo from "../../hooks/useSeo";
import Sparkline from "../../components/charts/Sparkline";
import TurkeyHeatmap from "../../components/charts/TurkeyHeatmap";

const CATEGORY_ICONS = {
    occupancy: BedDouble,
    hotel_count: Building2,
    investment: Wallet,
    tourism: Plane,
};

const COLORS = ["#39c8c1", "#d6b06b", "#123b52", "#22c55e", "#ef4444", "#0c2438"];

// Demo data for charts where no series exists yet
const MONTHS = ["Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"];
const OCCUPANCY_2024 = [40, 42, 48, 55, 62, 70, 82, 84, 76, 65, 55, 48];
const STAR_OCCUPANCY = [
    { star: "1★", value: 38 },
    { star: "2★", value: 46 },
    { star: "3★", value: 58 },
    { star: "4★", value: 67 },
    { star: "5★", value: 74 },
];
const STAR_DIST = [
    { name: "5 Yıldız", value: 612 },
    { name: "4 Yıldız", value: 1748 },
    { name: "3 Yıldız", value: 2360 },
    { name: "2 Yıldız", value: 4680 },
    { name: "1 Yıldız", value: 4928 },
];
const HOTELS_BY_YEAR = [
    { year: "2019", value: 12100 },
    { year: "2020", value: 11800 },
    { year: "2021", value: 12350 },
    { year: "2022", value: 13070 },
    { year: "2023", value: 13800 },
    { year: "2024", value: 14328 },
];
const TYPE_DIST = [
    { type: "Resort", value: 1840 },
    { type: "City", value: 5210 },
    { type: "Boutique", value: 1450 },
    { type: "Termal", value: 690 },
    { type: "Apart", value: 5138 },
];
const TOURISTS_BY_MONTH = [
    1.8, 2.1, 3.2, 4.4, 5.7, 6.8, 7.9, 7.6, 6.4, 5.2, 3.6, 2.7,
].map((v, i) => ({ month: MONTHS[i], value: v }));
const NATIONALITY = [
    { name: "Rusya", value: 18 },
    { name: "Almanya", value: 14 },
    { name: "İngiltere", value: 9 },
    { name: "İran", value: 5 },
    { name: "Bulgaristan", value: 4 },
    { name: "Diğer", value: 50 },
];

// Demo city occupancy values for heatmap
const CITY_OCCUPANCY = {
    "07": 78, "34": 71, "35": 66, "48": 74, "06": 64, "16": 58, "20": 55,
    "33": 52, "01": 49, "31": 47, "27": 50, "55": 56, "61": 60, "25": 44,
    "65": 41, "21": 46, "47": 48, "63": 51, "44": 47, "23": 43, "10": 52,
    "11": 39, "26": 53, "42": 54, "38": 55, "50": 62, "09": 68, "17": 57,
    "39": 36, "22": 38, "59": 40, "41": 49, "54": 47, "77": 45, "28": 50,
    "53": 58, "08": 49, "57": 47, "37": 41, "18": 35, "19": 38, "60": 41,
    "05": 39, "14": 50, "67": 44, "74": 38, "78": 36, "81": 42, "29": 33,
    "69": 30, "12": 32, "49": 30, "13": 31, "30": 27, "04": 28, "75": 25,
    "36": 30, "76": 28, "24": 35, "32": 49, "15": 45, "45": 50, "68": 41,
    "71": 38, "40": 36, "66": 35, "52": 47, "62": 32, "46": 41, "80": 43,
    "03": 51, "64": 42, "43": 40, "51": 36, "70": 33, "73": 26, "72": 34,
    "56": 29, "02": 36, "79": 38, "58": 37,
};

export default function DataHub() {
    const { t } = useTranslation();
    const [overview, setOverview] = useState([]);
    const [loading, setLoading] = useState(true);

    useSeo({
        title: "Veri Merkezi",
        description:
            "Türkiye otel sektörü veri merkezi. 81 il doluluk, ADR, RevPAR, yatırım hacmi ve turizm istatistikleri.",
        path: "/veri-merkezi",
    });

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/sector-data/overview");
                setOverview(data.items || []);
            } catch (_) {
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const updatedAt = useMemo(
        () =>
            overview.length
                ? new Date().toLocaleDateString("tr-TR", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                  })
                : "—",
        [overview],
    );

    return (
        <div className="bg-light-bg min-h-[60vh]" data-testid="data-hub-page">
            {/* HEADER */}
            <section className="bg-dark-navy text-white relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-[#08111d] via-[#0c2438] to-[#123b52]" />
                <div
                    className="absolute inset-0 opacity-[0.08]"
                    style={{
                        backgroundImage:
                            "radial-gradient(circle at 1px 1px, #39c8c1 1px, transparent 0)",
                        backgroundSize: "32px 32px",
                    }}
                />
                <div className="relative max-w-7xl mx-auto px-6 py-14 lg:py-20">
                    <span className="text-xs uppercase tracking-[0.25em] text-teal font-semibold">
                        {t("nav.data")}
                    </span>
                    <h1 className="mt-3 text-3xl sm:text-5xl font-bold tracking-tight">
                        Türkiye Otel Sektörü Veri Merkezi
                    </h1>
                    <p className="mt-4 text-white/75 max-w-2xl">
                        Bakanlık verilerine ve sektör araştırmalarına dayalı, 81 ili kapsayan canlı veri merkezi.
                    </p>
                    <div className="mt-5 inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-white/80">
                        <span className="inline-block w-2 h-2 rounded-full bg-data-green animate-pulse" />
                        Son güncelleme: <span className="font-data font-semibold text-white">{updatedAt}</span>
                    </div>
                </div>
            </section>

            {/* TOP KPI CARDS */}
            <section className="-mt-10 relative z-10">
                <div className="max-w-7xl mx-auto px-6">
                    {loading ? (
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                            {Array.from({ length: 4 }).map((_, i) => (
                                <div key={i} className="bg-white rounded-lg border border-slate-100 shadow-md h-28 animate-pulse" />
                            ))}
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="kpi-cards">
                            {overview.slice(0, 4).map((it) => (
                                <KpiCard key={it.id} item={it} />
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {/* OCCUPANCY SECTION */}
            <Section
                eyebrow="Doluluk"
                title="Doluluk Oranları"
                subtitle="Aylık doluluk eğrisi, il bazlı ısı haritası ve yıldız segmentlerinin karşılaştırması."
                source="TÜİK · KTB"
            >
                <div className="grid lg:grid-cols-3 gap-6">
                    <div className="bg-white rounded-lg border border-slate-100 shadow-md p-5 lg:col-span-2">
                        <h3 className="text-sm font-semibold uppercase tracking-wider text-deep-blue mb-3">
                            Aylık Doluluk (2024)
                        </h3>
                        <div className="h-64">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={OCCUPANCY_2024.map((v, i) => ({ month: MONTHS[i], value: v }))}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                    <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#64748b" />
                                    <YAxis unit="%" tick={{ fontSize: 11 }} stroke="#64748b" />
                                    <Tooltip
                                        contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }}
                                        formatter={(v) => `${v}%`}
                                    />
                                    <Line
                                        type="monotone"
                                        dataKey="value"
                                        stroke="#39c8c1"
                                        strokeWidth={3}
                                        dot={{ r: 4, fill: "#39c8c1" }}
                                        activeDot={{ r: 6 }}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                    <div className="bg-white rounded-lg border border-slate-100 shadow-md p-5">
                        <h3 className="text-sm font-semibold uppercase tracking-wider text-deep-blue mb-3">
                            Yıldız Segmenti
                        </h3>
                        <div className="h-64">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={STAR_OCCUPANCY}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                    <XAxis dataKey="star" tick={{ fontSize: 11 }} stroke="#64748b" />
                                    <YAxis unit="%" tick={{ fontSize: 11 }} stroke="#64748b" />
                                    <Tooltip formatter={(v) => `${v}%`} contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                                    <Bar dataKey="value" fill="#d6b06b" radius={[6, 6, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>

                <div className="mt-6 bg-white rounded-lg border border-slate-100 shadow-md p-5">
                    <TurkeyHeatmap
                        title="İl Bazlı Doluluk"
                        values={CITY_OCCUPANCY}
                        unit="%"
                    />
                </div>
            </Section>

            {/* HOTEL COUNT SECTION */}
            <Section
                eyebrow="Tesis"
                title="Otel Sayıları"
                subtitle="Yıllık trend, yıldız dağılımı ve tesis türü bazında kompozisyon."
                source="Kültür ve Turizm Bakanlığı"
                alt
            >
                <div className="grid lg:grid-cols-3 gap-6">
                    <div className="bg-white rounded-lg border border-slate-100 shadow-md p-5">
                        <h3 className="text-sm font-semibold uppercase tracking-wider text-deep-blue mb-3">
                            Yıldız Dağılımı
                        </h3>
                        <div className="h-64">
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={STAR_DIST}
                                        innerRadius={50}
                                        outerRadius={88}
                                        dataKey="value"
                                        nameKey="name"
                                        paddingAngle={2}
                                    >
                                        {STAR_DIST.map((_, i) => (
                                            <Cell key={i} fill={COLORS[i % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                        <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
                            {STAR_DIST.map((d, i) => (
                                <div key={d.name} className="flex items-center gap-2">
                                    <span className="w-2.5 h-2.5 rounded-sm" style={{ background: COLORS[i] }} />
                                    <span className="text-deep-blue/80">{d.name}</span>
                                    <span className="ml-auto font-data text-dark-navy font-semibold">{d.value.toLocaleString("tr-TR")}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="bg-white rounded-lg border border-slate-100 shadow-md p-5 lg:col-span-2">
                        <h3 className="text-sm font-semibold uppercase tracking-wider text-deep-blue mb-3">
                            Yıllara Göre Belgeli Tesis Sayısı
                        </h3>
                        <div className="h-64">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={HOTELS_BY_YEAR}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                    <XAxis dataKey="year" tick={{ fontSize: 11 }} stroke="#64748b" />
                                    <YAxis tick={{ fontSize: 11 }} stroke="#64748b" />
                                    <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                                    <Line type="monotone" dataKey="value" stroke="#123b52" strokeWidth={3} dot={{ r: 4, fill: "#123b52" }} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>

                <div className="mt-6 bg-white rounded-lg border border-slate-100 shadow-md p-5">
                    <h3 className="text-sm font-semibold uppercase tracking-wider text-deep-blue mb-3">
                        Tesis Türü Dağılımı
                    </h3>
                    <div className="h-56">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={TYPE_DIST} layout="vertical">
                                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                <XAxis type="number" tick={{ fontSize: 11 }} stroke="#64748b" />
                                <YAxis type="category" dataKey="type" tick={{ fontSize: 11 }} stroke="#64748b" width={80} />
                                <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                                <Bar dataKey="value" fill="#39c8c1" radius={[0, 6, 6, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </Section>

            {/* INVESTMENT SECTION */}
            <Section
                eyebrow="Yatırım"
                title="Yatırım Verileri"
                subtitle="Oda başına maliyet, bölgesel yoğunluk ve geri dönüş süreleri."
                source="TUYED · Sektör Raporları"
            >
                <div className="grid md:grid-cols-3 gap-6">
                    <KpiBox
                        Icon={Wallet}
                        title="Oda Başına Maliyet (5★)"
                        value={120}
                        prefix="$"
                        suffix="K"
                        trend="up"
                        pct={6.2}
                        sub="Avg. CapEx, Q1 2025"
                    />
                    <KpiBox
                        Icon={MapPin}
                        title="Bölgesel Yoğunluk"
                        value={42}
                        suffix="%"
                        trend="stable"
                        pct={0}
                        sub="Akdeniz + Ege payı"
                    />
                    <KpiBox
                        Icon={TrendingUp}
                        title="Ortalama Geri Dönüş"
                        value={7.2}
                        decimals={1}
                        suffix=" yıl"
                        trend="down"
                        pct={4.1}
                        sub="ROI medyan, 4-5★"
                    />
                </div>
            </Section>

            {/* TOURISM SECTION */}
            <Section
                eyebrow="Turizm"
                title="Turizm Genel Görünüm"
                subtitle="Aylık turist sayıları, milliyet dağılımı ve geceleme trendleri."
                source="TÜİK · KTB"
                alt
            >
                <div className="grid lg:grid-cols-3 gap-6">
                    <div className="bg-white rounded-lg border border-slate-100 shadow-md p-5 lg:col-span-2">
                        <h3 className="text-sm font-semibold uppercase tracking-wider text-deep-blue mb-3">
                            Aylık Turist Sayısı (Milyon)
                        </h3>
                        <div className="h-64">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={TOURISTS_BY_MONTH}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                    <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#64748b" />
                                    <YAxis tick={{ fontSize: 11 }} stroke="#64748b" />
                                    <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} formatter={(v) => `${v}M`} />
                                    <Bar dataKey="value" fill="#39c8c1" radius={[6, 6, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                    <div className="bg-white rounded-lg border border-slate-100 shadow-md p-5">
                        <h3 className="text-sm font-semibold uppercase tracking-wider text-deep-blue mb-3">
                            Milliyet Dağılımı
                        </h3>
                        <div className="h-64">
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={NATIONALITY}
                                        innerRadius={42}
                                        outerRadius={88}
                                        dataKey="value"
                                        nameKey="name"
                                        paddingAngle={1}
                                    >
                                        {NATIONALITY.map((_, i) => (
                                            <Cell key={i} fill={COLORS[i % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip formatter={(v) => `${v}%`} contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>
            </Section>
        </div>
    );
}

function KpiCard({ item }) {
    const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.3 });
    const Icon = CATEGORY_ICONS[item.category] || BarChart3;
    const TrendIcon = item.trend_direction === "up" ? TrendingUp : item.trend_direction === "down" ? TrendingDown : Minus;
    const trendColor =
        item.trend_direction === "up"
            ? "text-data-green"
            : item.trend_direction === "down"
            ? "text-data-red"
            : "text-deep-blue/60";
    const name = (item.name && (item.name.tr || Object.values(item.name)[0])) || "—";

    // synthesize a sparkline shape based on trend
    const series = useMemo(() => {
        const base = item.value || 1;
        const dir = item.trend_direction === "down" ? -1 : item.trend_direction === "up" ? 1 : 0;
        return Array.from({ length: 12 }, (_, i) => base * (1 + dir * 0.06 * Math.sin((i / 11) * Math.PI) * (1 + i / 30)));
    }, [item]);

    return (
        <div
            ref={ref}
            data-testid={`kpi-card-${item.category}`}
            className="lift-card bg-white rounded-lg border border-slate-100 shadow-md hover:shadow-xl p-5"
        >
            <div className="flex items-start justify-between">
                <div>
                    <span className="inline-flex w-9 h-9 rounded-md bg-teal/10 text-teal-700 items-center justify-center">
                        <Icon className="w-4 h-4" />
                    </span>
                    <h3 className="mt-3 text-xs uppercase tracking-wider text-deep-blue/80 font-semibold">
                        {name}
                    </h3>
                </div>
                <span className={`inline-flex items-center gap-1 text-xs font-data font-semibold ${trendColor}`}>
                    <TrendIcon className="w-3.5 h-3.5" />
                    {item.trend_percentage}%
                </span>
            </div>
            <div className="mt-3 font-data text-3xl font-bold text-dark-navy tabular-nums">
                {inView ? (
                    <CountUp end={item.value} duration={1.6} decimals={item.value % 1 === 0 ? 0 : 1} separator="." decimal="," />
                ) : (
                    "0"
                )}
                <span className="ml-1 text-base text-deep-blue/70 font-semibold">{item.unit}</span>
            </div>
            <div className="mt-2 h-7">
                <Sparkline data={series} color={item.trend_direction === "down" ? "#ef4444" : "#39c8c1"} />
            </div>
            {item.source_reference && (
                <p className="mt-2 text-[11px] text-deep-blue/60 truncate">{item.source_reference}</p>
            )}
        </div>
    );
}

function Section({ eyebrow, title, subtitle, source, children, alt }) {
    return (
        <section className={`py-16 ${alt ? "bg-white" : "bg-light-bg"}`}>
            <div className="max-w-7xl mx-auto px-6">
                <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-3">
                    <div>
                        <span className="text-xs uppercase tracking-[0.2em] text-teal-700 font-semibold">{eyebrow}</span>
                        <h2 className="mt-2 text-2xl sm:text-3xl font-bold text-dark-navy">{title}</h2>
                        {subtitle && <p className="mt-2 text-deep-blue/80 max-w-2xl text-sm">{subtitle}</p>}
                    </div>
                    {source && (
                        <span className="text-xs text-deep-blue/60 font-semibold uppercase tracking-wider">
                            Kaynak: {source}
                        </span>
                    )}
                </div>
                {children}
            </div>
        </section>
    );
}

function KpiBox({ Icon, title, value, prefix, suffix, decimals = 0, trend, pct, sub }) {
    const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.3 });
    const TrendIcon = trend === "up" ? TrendingUp : trend === "down" ? TrendingDown : Minus;
    const tColor = trend === "up" ? "text-data-green" : trend === "down" ? "text-data-red" : "text-deep-blue/50";
    return (
        <div ref={ref} className="lift-card bg-white rounded-lg border border-slate-100 shadow-md hover:shadow-xl p-5">
            <span className="inline-flex w-10 h-10 rounded-md bg-gold/10 text-gold-700 items-center justify-center">
                <Icon className="w-5 h-5" />
            </span>
            <h3 className="mt-3 text-xs uppercase tracking-wider text-deep-blue/80 font-semibold">{title}</h3>
            <div className="mt-2 font-data text-3xl font-bold text-dark-navy tabular-nums">
                {prefix}
                {inView ? <CountUp end={value} decimals={decimals} duration={1.6} /> : 0}
                {suffix}
            </div>
            <div className="mt-1 flex items-center justify-between">
                <span className="text-xs text-deep-blue/60">{sub}</span>
                {pct !== 0 && (
                    <span className={`inline-flex items-center gap-1 text-xs font-data font-semibold ${tColor}`}>
                        <TrendIcon className="w-3 h-3" /> {pct}%
                    </span>
                )}
            </div>
        </div>
    );
}
